import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import {
  DELETEProviderAffiliation,
  GETProvidersForAffiliation,
  GETProviderAffiliations,
  POSTProviderAffiliation,
  PUTProviderAffiliation,
  GETProviderAffiliationById,
  DELETEProviderFromAffiliation,
  GETAvailableProvidersForAffiliation,
  POSTAddProvidersToAffiliation,
  POSTImportProvidersToAffiliation,
} from './requests.ts'
import {
  CreateProviderAffiliation,
  ProviderAffiliation,
  UpdateProviderAffiliation,
  DeleteProviderAffiliationResponse,
  PaginatedAffiliationProviders,
  RemoveProviderFromAffiliationResponse,
  AddProvidersToAffiliationResponse,
} from './schema.ts'
import { RequestError } from '@/app/common/types_and_interfaces.ts'
import { ProviderQueryKeys } from '@/queries/providers/queries'

const queryKey = ['admin_provider_affiliations']

export function useProviderAffiliations(enabled: boolean = true) {
  const queryClient = useQueryClient()

  const query = useQuery<ProviderAffiliation[]>({
    queryKey,
    queryFn: GETProviderAffiliations,
    refetchOnMount: 'always',
    enabled,
  })

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey })

  return { query, invalidateQuery }
}

export function usePostProviderAffiliation(
  options?: UseMutationOptions<
    ProviderAffiliation,
    RequestError,
    CreateProviderAffiliation
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateProviderAffiliation) =>
      POSTProviderAffiliation(data),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ProviderQueryKeys.details_all })
      options?.onSuccess?.(...args)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function usePutProviderAffiliation(
  options?: UseMutationOptions<
    ProviderAffiliation,
    RequestError,
    { id: number; data: UpdateProviderAffiliation }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => PUTProviderAffiliation(id, data),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ProviderQueryKeys.details_all })
      options?.onSuccess?.(...args)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useDeleteProviderAffiliation(
  options?: UseMutationOptions<
    DeleteProviderAffiliationResponse,
    RequestError,
    number
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => DELETEProviderAffiliation(id),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ProviderQueryKeys.details_all })
      options?.onSuccess?.(...args)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useProviderAffiliationDetail(
  affiliationId: number,
  enabled: boolean = true
) {
  return useQuery<ProviderAffiliation>({
    queryKey: ['admin_provider_affiliations', 'detail', affiliationId],
    queryFn: () => GETProviderAffiliationById(affiliationId),
    enabled: enabled && Boolean(affiliationId),
  })
}

type UseProvidersForAffiliationParams = {
  affiliationId: number
  programId: number
  currentPage: number
  recordsPerPage: number
  search?: string
  enabled?: boolean
}

export function useProvidersForAffiliation(
  params: UseProvidersForAffiliationParams
) {
  const {
    affiliationId,
    programId,
    currentPage,
    recordsPerPage,
    search,
    enabled = true,
  } = params

  return useQuery<PaginatedAffiliationProviders>({
    queryKey: [
      'admin_provider_affiliations',
      'providers',
      affiliationId,
      programId,
      currentPage,
      recordsPerPage,
      search,
    ],
    queryFn: () => GETProvidersForAffiliation(params),
    enabled: enabled && Boolean(affiliationId && programId),
    placeholderData: keepPreviousData,
  })
}

type UseAvailableProvidersForAffiliationParams = {
  affiliationId: number
  programId: number
  currentPage: number
  recordsPerPage: number
  search?: string
  enabled?: boolean
}

export function useAvailableProvidersForAffiliation(
  params: UseAvailableProvidersForAffiliationParams
) {
  const {
    affiliationId,
    programId,
    currentPage,
    recordsPerPage,
    search,
    enabled = true,
  } = params

  return useQuery<PaginatedAffiliationProviders>({
    queryKey: [
      'admin_provider_affiliations',
      'available_providers',
      affiliationId,
      programId,
      currentPage,
      recordsPerPage,
      search,
    ],
    queryFn: () => GETAvailableProvidersForAffiliation(params),
    enabled: enabled && Boolean(affiliationId && programId),
    placeholderData: keepPreviousData,
  })
}

export function useRemoveProviderFromAffiliation(
  options?: UseMutationOptions<
    RemoveProviderFromAffiliationResponse,
    RequestError,
    { affiliationId: number; providerId: number; programId: number }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      affiliationId: number
      providerId: number
      programId: number
    }) => DELETEProviderFromAffiliation(params),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ProviderQueryKeys.details_all })
      options?.onSuccess?.(...args)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useAddProvidersToAffiliation(
  options?: UseMutationOptions<
    AddProvidersToAffiliationResponse,
    RequestError,
    { affiliationId: number; programId: number; providerIds: number[] }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      affiliationId: number
      programId: number
      providerIds: number[]
    }) => POSTAddProvidersToAffiliation(params),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ProviderQueryKeys.details_all })
      options?.onSuccess?.(...args)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

export function useImportProvidersToAffiliation(
  options?: UseMutationOptions<
    AddProvidersToAffiliationResponse,
    RequestError,
    { affiliationId: number; programId: number; file: File }
  >
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      affiliationId: number
      programId: number
      file: File
    }) => POSTImportProvidersToAffiliation(params),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({
        queryKey: ProviderQueryKeys.details_all,
      })
      options?.onSuccess?.(...args)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}
